<template>
  <div class="py-3">
    <div class="p-toolbar">
      <div class="p-toolbar-item p-search">
        <p-search-input
          v-model="search"
          :items="searchSuggestions"
        ></p-search-input>
      </div>
      <div v-if="hasAccess()" class="p-toolbar-item p-right">
        <v-btn
          v-if="hasAccess('write', 'settings')"
          :to="{ name: 'settingsScheduleTemplates' }"
          color="primary"
          class="p-btn-text mr-4"
          text
          large
        >
          {{ $t('schedule.templates') }}
        </v-btn>
        <v-btn
          large
          rounded
          class="p-btn-rounded mr-1"
          @click.stop="openCreateDialog()"
        >
          {{ $t('schedule.createSchedule') }}
        </v-btn>
      </div>
    </div>

    <v-row>
      <v-col>
        <v-data-table
          :headers="tableHeaders"
          :items="schedulesComputed"
          :search="search"
          :page.sync="page"
          :items-per-page="itemsPerPage"
          hide-default-footer
          no-data-text=""
          class="p-no-borders"
          @page-count="pageCount = $event"
        >
          <template v-slot:[`item.changed`]="{ item }">
            <v-tooltip v-if="item.changed" bottom>
              <template v-slot:activator="{ on }">
                <v-btn icon v-on="on">
                  <span v-if="item.changed" class="item-changed"></span>
                </v-btn>
              </template>
              {{ $t('schedule.table.changedTooltip') }}
            </v-tooltip>
          </template>
          <template v-slot:[`item.created`]="{ item }">
            {{ formatDate(item.created) }}
          </template>
          <template v-slot:[`item.pdf`]="{ item }">
            <v-btn icon @click="downloadPdf(item)">
              <p-file-icon extension="'pdf'"></p-file-icon>
            </v-btn>
          </template>
          <template v-if="hasAccess()" v-slot:[`item.actions`]="{ item }">
            <div class="p-table-row-actions p-visible-on-row-hover">
              <p-btn-icon type="edit" @click="openEditDialog(item)"></p-btn-icon>
              <p-btn-icon type="delete" @click="deleteSchedule_(item)"></p-btn-icon>
            </div>
          </template>
          <template v-slot:[`no-data`]>
            <p-no-data
              v-if="schedulesByProjectLoaded(currentProjectId)"
              :text="$t('schedule.noSchedules')"
              :btn-text="$t('schedule.createSchedule')"
              :hide-btn="!hasAccess()"
              img="schedule.png"
              class="p-no-data-mt"
              @btn-click="openCreateDialog()"
            ></p-no-data>
          </template>
        </v-data-table>

        <v-pagination
          v-if="schedulesByProject(currentProjectId).length > 0"
          v-model="page"
          :length="pageCount"
          :total-visible="15"
          class="mt-5"
        ></v-pagination>
      </v-col>
    </v-row>

    <schedule-create-dialog
      :dialog.sync="createDialog"
    ></schedule-create-dialog>

    <schedule-edit-dialog
      :dialog.sync="editDialog"
      :schedule="editedSchedule"
    ></schedule-edit-dialog>
  </div>
</template>


<script>
import { mapGetters, mapActions } from 'vuex'

import moment from 'moment'

import fileHelper from '@/helpers/file.helper'

import PFileIcon from '@/components/PFileIcon'
import PSearchInput from '@/components/PSearchInput'
import PNoData from '@/components/PNoData'
import PBtnIcon from '@/components/PBtnIcon'

import ScheduleCreateDialog from './ScheduleCreateDialog'
import ScheduleEditDialog from './ScheduleEditDialog'

import snackbarMixin from '@/mixins/snackbar.mixin'
import pageMixin from '@/mixins/page.mixin'
import objectMixin from '@/mixins/object.mixin'
import accessMixin from '@/mixins/access.mixin'

import eventBus from '@/eventBus'

export default {
  components: {
    PFileIcon,
    PSearchInput,
    PNoData,
    PBtnIcon,
    ScheduleCreateDialog,
    ScheduleEditDialog
  },


  mixins: [
    snackbarMixin,
    pageMixin,
    objectMixin,
    accessMixin
  ],


  data () {
    return {
      search: '',
      page: 1,
      pageCount: 0,
      itemsPerPage: 20,
      createDialog: false,
      editDialog: false,
      editedSchedule: null
    }
  },


  computed: {
    ...mapGetters('schedule', ['schedulesByProject', 'schedulesByProjectLoaded']),
    ...mapGetters('contact', ['shortContactById']),
    ...mapGetters('project', ['currentProjectId']),
    ...mapGetters('changes', ['isItemChanged']),

    schedulesComputed () {
      return this.schedulesByProject(this.currentProjectId).map(schedule => {
        return {
          ...schedule,
          changed: this.isItemChanged(schedule.id)
        }
      })
    },

    tableHeaders () {
      return [
        { text: this.$t('schedule.table.name'), value: 'name' },
        { text: '', value: 'changed' },
        // { text: this.$t('schedule.table.fileSize'), value: 'size' },
        { text: this.$t('schedule.table.created'), value: 'createdBy' },
        // { text: this.$t('schedule.table.lastModified'), value: 'lastModified' },
        // { text: this.$t('schedule.table.shared'), value: 'devided' },
        { text: this.$t('schedule.table.email'), value: 'email' },
        { text: this.$t('schedule.table.download'), value: 'pdf', sortable: false, filterable: false },
        { text: '', value: 'actions', align: 'end', sortable: false, filterable: false }
      ]
    },

    searchSuggestions () {
      const items = this.schedulesByProject(this.currentProjectId).map(schedule => { return schedule.name })
      return items
    }
  },


  watch: {
    async currentProjectId () {
      if (!this.currentProjectId) return false
      this.startPageLoading()
      this.loadMembersByProject(this.currentProjectId)
      this.loadEventsByProject(this.currentProjectId)
      this.loadEventCategories()
      await this.loadSchedulesByProject(this.currentProjectId)
      this.stopPageLoading()
    }
  },


  methods: {
    ...mapActions('schedule', ['loadSchedulesByProject', 'deleteSchedule', 'getSchedulePdf']),
    ...mapActions('contact', ['loadShortContacts']),
    ...mapActions('member', ['loadMembersByProject']),
    ...mapActions('event', ['loadEventsByProject']),
    ...mapActions('scheduleTemplate', ['loadScheduleTemplates']),
    ...mapActions('user', ['loadUsersShort']),
    ...mapActions('eventCategory', ['loadEventCategories']),

    async searchSchedules (val) {
      this.search = val
    },

    initEventBus () {
      eventBus.$on('goToEntity', async (entityTitle) => {
        this.searchSchedules(entityTitle)
      })

      eventBus.$on('loadEntities', async () => {
        await this.loadSchedulesByProject(this.currentProjectId)
      })
    },

    async downloadPdf (schedule) {
      try {
        const pdf = await this.getSchedulePdf(schedule)
        const blob = new Blob([pdf], { type: 'application/pdf' })
        fileHelper.downloadFile(blob, schedule.name || 'schedule')
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    },

    formatDate (date) {
      return date ? moment(date).format('MMM D, yyyy') : ''
    },

    openCreateDialog () {
      this.createDialog = true
    },

    openEditDialog (schedule) {
      this.editedSchedule = schedule
      this.editDialog = true
    },

    async deleteSchedule_ (schedule) {
      try {
        const confirm = await this.$confirm(this.$t('schedule.deleteScheduleConfirm', { title: schedule.name }), { trueText: this.$t('delete') })
        if (confirm === true) {
          this.showSnackbarProcessing()
          await this.deleteSchedule(schedule)
          this.showSnackbarSuccess(this.$t('schedule.scheduleDeleted', { title: schedule.name }))
        }
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    }
  },


  async created () {
    this.initEventBus()
    if (!this.currentProjectId) return false
    try {
      this.startPageLoading()
      this.loadShortContacts()
      if (this.hasAccess('read', 'settings')) {
        this.loadScheduleTemplates({ skip: 0 })
      }
      this.loadMembersByProject(this.currentProjectId)
      this.loadEventsByProject(this.currentProjectId)
      this.loadEventCategories()
      this.loadUsersShort()
      await this.loadSchedulesByProject(this.currentProjectId)
      if (this.$router.currentRoute.params?.goto) {
        await this.searchSchedules(this.$router.currentRoute.params?.goto)
      }
    } catch (error) {
      this.showSnackbarError()
      this.$log.error(error)
    } finally {
      this.stopPageLoading()
    }
  }
}
</script>
